import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert, CircularProgress,
  InputAdornment, FormControlLabel, Checkbox, Switch,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';

const PLAN_TYPES = ['punchcard', 'recurring', 'drop-in', 'private', 'single'];
const ATHLETE_TYPES = ['junior', 'adult', 'both'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'club', 'all'];
// Default groups seeded into Firestore on first load if no planGroups collection exists
const DEFAULT_PLAN_GROUPS = [
  { name: 'Single Sessions', displayOrder: 1, description: '' },
  { name: 'Session Packs', displayOrder: 2, description: '' },
  { name: 'Monthly Memberships', displayOrder: 3, description: '' },
  { name: 'Adult Plans', displayOrder: 4, description: '' },
  { name: 'Other', displayOrder: 99, description: '' },
];
const CLASS_TYPES = [
  { value: 'junior-practice', label: 'Junior Practice' },
  { value: 'junior-private', label: 'Junior Private' },
  { value: 'junior-drop-in', label: 'Junior Drop-in' },
  { value: 'junior-evaluation', label: 'Junior Evaluation' },
  { value: 'adult-practice', label: 'Adult Practice' },
];

const emptyPlan = {
  planName: '',
  planType: 'punchcard',
  athleteType: 'junior',
  level: [],
  price: '',
  sessionsIncluded: '',
  description: '',
  active: true,
  allowMiniReg: false,
  sortOrder: '',
  rollover: true,
  planGroup: 'Session Packs',
  slug: '',
  promptReserve: false,
  reserveClassType: '',
  stripePriceId: '',
  waiverDocId: '', // id of a clubDocuments doc gating purchase of this plan
};

function Payments() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planGroups, setPlanGroups] = useState([]);
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', displayOrder: 1, description: '' });
  const [loading, setLoading] = useState(true);
  const [planDialog, setPlanDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [editingPlanId, setEditingPlanId] = useState(null);
  // Eligible clubDocuments for the waiver dropdown. Filtered to enabled docs
  // with an acceptance-style actionType — same rule as the membership gate.
  const [waiverDocs, setWaiverDocs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) setIsAdmin((userDoc.data().roles || []).includes('admin'));
      }
      const planSnap = await getDocs(collection(db, 'plans'));
      setPlans(planSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load plan groups, seeding defaults if collection is empty
      let groupSnap = await getDocs(collection(db, 'planGroups'));
      if (groupSnap.empty) {
        await Promise.all(DEFAULT_PLAN_GROUPS.map(g =>
          setDoc(doc(db, 'planGroups', g.name), g)
        ));
        groupSnap = await getDocs(collection(db, 'planGroups'));
      }
      const groups = groupSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      groups.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
      setPlanGroups(groups);

      // Load eligible documents for the per-plan waiver dropdown.
      try {
        const docsSnap = await getDocs(collection(db, 'clubDocuments'));
        setWaiverDocs(
          docsSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(d => d.enabled !== false && (d.actionType === 'accept' || d.actionType === 'signature'))
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        );
      } catch (e) {
        // Non-fatal: dropdown will just be empty. Plans without waivers still
        // behave normally.
        console.warn('Failed to load clubDocuments:', e);
      }

      setLoading(false);
    };
    fetchData();
  }, []);

  const handleSavePlan = async () => {
    if (!planForm.planName || !planForm.price) {
      setError('Please fill in plan name and price.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const planData = {
        ...planForm,
        price: parseFloat(planForm.price),
        sessionsIncluded: planForm.sessionsIncluded ? parseInt(planForm.sessionsIncluded) : null,
        sortOrder: planForm.sortOrder !== '' && planForm.sortOrder != null ? parseInt(planForm.sortOrder) : null,
      };
      if (editingPlanId) {
        await updateDoc(doc(db, 'plans', editingPlanId), planData);
        setPlans(prev => prev.map(p => p.id === editingPlanId ? { id: editingPlanId, ...planData } : p));
        setSuccess('Plan updated successfully.');
      } else {
        planData.createdAt = new Date();
        const ref = await addDoc(collection(db, 'plans'), planData);
        setPlans(prev => [...prev, { id: ref.id, ...planData }]);
        setSuccess('Plan created successfully.');
      }
      setPlanDialog(false);
      setPlanForm(emptyPlan);
      setEditingPlanId(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Payments', err.message || String(err), err.stack || '');
      setError('Failed to save plan.');
    }
    setSaving(false);
  };

  const handleEditPlan = (plan) => {
    setPlanForm({
      planName: plan.planName,
      planType: plan.planType,
      athleteType: plan.athleteType,
      level: Array.isArray(plan.level) ? plan.level : (plan.level ? [plan.level] : []),
      price: String(plan.price ?? ''),
      sessionsIncluded: plan.sessionsIncluded != null ? String(plan.sessionsIncluded) : '',
      description: plan.description || '',
      active: plan.active,
      allowMiniReg: plan.allowMiniReg === true,
      sortOrder: plan.sortOrder != null ? String(plan.sortOrder) : '',
      rollover: plan.rollover !== false,
      planGroup: plan.planGroup || 'Session Packs',
      slug: plan.slug || plan.planName?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || '',
      promptReserve: plan.promptReserve || false,
      reserveClassType: plan.reserveClassType || '',
      stripePriceId: plan.stripePriceId || '',
      waiverDocId: plan.waiverDocId || '',
    });
    setEditingPlanId(plan.id);
    setPlanDialog(true);
  };

  const handleDuplicatePlan = async (plan) => {
    try {
      const planData = {
        planName: plan.planName + ' (copy)',
        planType: plan.planType,
        athleteType: plan.athleteType,
        price: plan.price,
        sessionsIncluded: plan.sessionsIncluded,
        description: plan.description || '',
        active: plan.active,
        allowMiniReg: plan.allowMiniReg === true,
        sortOrder: plan.sortOrder ?? null,
        rollover: plan.rollover !== false,
        planGroup: plan.planGroup || 'Session Packs',
        promptReserve: plan.promptReserve || false,
        reserveClassType: plan.reserveClassType || '',
        waiverDocId: plan.waiverDocId || '',
        createdAt: new Date(),
      };
      const ref = await addDoc(collection(db, 'plans'), planData);
      setPlans(prev => [...prev, { id: ref.id, ...planData }]);
      setSuccess('Plan duplicated.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Payments', err.message || String(err), err.stack || '');
      setError('Failed to duplicate plan.');
    }
  };

  const handleTogglePlanActive = async (plan) => {
    const newActive = !plan.active;
    try {
      await updateDoc(doc(db, 'plans', plan.id), { active: newActive });
      setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, active: newActive } : p));
      setSuccess(`Plan ${newActive ? 'activated' : 'deactivated'}.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Payments', err.message || String(err), err.stack || '');
      setError('Failed to update plan.');
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm('Delete "' + plan.planName + '"? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'plans', plan.id));
      setPlans(prev => prev.filter(p => p.id !== plan.id));
      setSuccess('Plan deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Payments', err.message || String(err), err.stack || '');
      setError('Failed to delete plan.');
    }
  };

  const closePlanDialog = () => {
    setPlanDialog(false);
    setPlanForm(emptyPlan);
    setEditingPlanId(null);
    setError('');
  };

  // ── Plan Group management ────────────────────────────────────────────────
  const openNewGroup = () => {
    const nextOrder = planGroups.length > 0 ? Math.max(...planGroups.map(g => g.displayOrder ?? 0)) + 1 : 1;
    setEditingGroup(null);
    setGroupForm({ name: '', displayOrder: nextOrder, description: '' });
    setGroupDialog(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name || group.id,
      displayOrder: group.displayOrder ?? 1,
      description: group.description || '',
    });
    setGroupDialog(true);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) { setError('Group name is required.'); return; }
    try {
      const data = {
        name: groupForm.name.trim(),
        displayOrder: parseInt(groupForm.displayOrder) || 1,
        description: groupForm.description?.trim() || '',
      };
      // Use the name as the doc id for uniqueness and easy lookup
      const docId = editingGroup ? editingGroup.id : data.name;
      await setDoc(doc(db, 'planGroups', docId), data);
      const newGroup = { id: docId, ...data };
      setPlanGroups(prev => {
        const without = prev.filter(g => g.id !== docId);
        const next = [...without, newGroup];
        next.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        return next;
      });
      setGroupDialog(false);
      setEditingGroup(null);
      setGroupForm({ name: '', displayOrder: 1, description: '' });
      setSuccess(editingGroup ? 'Group updated.' : 'Group created.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Payments', err.message || String(err), err.stack || '');
      setError('Failed to save group.');
    }
  };

  const handleDeleteGroup = async (group) => {
    const planCount = plans.filter(p => (p.planGroup || 'Session Packs') === group.name).length;
    const msg = planCount > 0
      ? `Delete "${group.name}"? ${planCount} plan${planCount > 1 ? 's' : ''} will become uncategorized (move to "Other"). This cannot be undone.`
      : `Delete "${group.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteDoc(doc(db, 'planGroups', group.id));
      setPlanGroups(prev => prev.filter(g => g.id !== group.id));
      setSuccess('Group deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('Payments', err.message || String(err), err.stack || '');
      setError('Failed to delete group.');
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>

        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Plans</Typography>
            <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>Manage session plans and pricing.</Typography>
          </Box>
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewGroup}>
                New Group
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setPlanDialog(true)}>
                New Plan
              </Button>
            </Box>
          )}
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        {error   && <Alert severity="error"   sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : plans.length === 0 ? (
          <Typography variant="body1" sx={{ color: '#aaa', textAlign: 'center', py: 8 }}>
            No plans created yet. Click "New Plan" to get started.
          </Typography>
        ) : (
          <Box>
            {planGroups
              .filter(g => plans.some(p => (p.planGroup || 'Session Packs') === g.name))
              .map(group => (
                <Box key={group.id} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, pb: 1, borderBottom: '2px solid #DEEAF1' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
                        {group.name}
                      </Typography>
                      {group.description && (
                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                          {group.description}
                        </Typography>
                      )}
                    </Box>
                    {isAdmin && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditGroup(group)}
                                sx={{ minWidth: 0, fontSize: '0.7rem' }}>
                          Edit Group
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {plans
                      .filter(p => (p.planGroup || 'Session Packs') === group.name)
                      .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || (a.price ?? 0) - (b.price ?? 0))
                      .map(plan => (
                        <Paper key={plan.id} elevation={1} sx={{ p: 3, borderRadius: 2, border: '1px solid #eee', flex: '1 1 250px', maxWidth: 350 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{plan.planName}</Typography>
                            <Chip
                              label={plan.active ? 'Active' : 'Inactive'}
                              size="small"
                              onClick={() => handleTogglePlanActive(plan)}
                              sx={{
                                backgroundColor: plan.active ? '#E9F7EF' : '#f0f0f0',
                                color: plan.active ? '#1E6B3C' : '#888',
                                cursor: 'pointer',
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: plan.active ? '#d4efdf' : '#e0e0e0',
                                  boxShadow: '0 0 0 2px rgba(30,107,60,0.15)',
                                },
                              }}
                              title={plan.active ? 'Click to deactivate (hide from public pages)' : 'Click to activate (show on public pages)'}
                            />
                          </Box>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#27AE60', mb: 1 }}>
                            ${plan.price}{plan.planType === 'recurring' ? '/mo' : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Chip label={plan.planType} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                            <Chip label={plan.athleteType} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                            {(() => {
                              const levels = Array.isArray(plan.level) ? plan.level : (plan.level ? [plan.level] : []);
                              return levels.map(l => (
                                <Chip key={l} label={l} size="small" variant="outlined"
                                      sx={{ textTransform: 'capitalize', borderColor: '#1976d2', color: '#1976d2' }} />
                              ));
                            })()}
                            {plan.planType === 'recurring' && (
                              <Chip label={plan.rollover !== false ? 'Rollover' : 'Reset'} size="small" variant="outlined" />
                            )}
                          </Box>
                          {plan.sessionsIncluded && (
                            <Typography variant="body2" sx={{ color: '#555' }}>{plan.sessionsIncluded} sessions included</Typography>
                          )}
                          {plan.description && (
                            <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>{plan.description}</Typography>
                          )}
                          {isAdmin && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, borderTop: '1px solid #f0f0f0', pt: 2, flexWrap: 'wrap' }}>
                              <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditPlan(plan)}>Edit</Button>
                              <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => handleDuplicatePlan(plan)}>Duplicate</Button>
                              {plan.slug && (
                                <Button size="small" startIcon={<ContentCopyIcon />}
                                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/p/${plan.slug}`); }}>
                                  Copy Link
                                </Button>
                              )}
                              <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => handleDeletePlan(plan)}>Delete</Button>
                            </Box>
                          )}
                        </Paper>
                      ))}
                  </Box>
                </Box>
              ))}
          </Box>
        )}

        {/* Plan Dialog */}
        <Dialog open={planDialog} onClose={closePlanDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>
            {editingPlanId ? 'Edit Plan' : 'Create New Plan'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="Plan Name *" value={planForm.planName}
                onChange={e => {
                  const name = e.target.value;
                  const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                  setPlanForm(p => ({ ...p, planName: name, slug: p.slug && p.slug !== autoSlug.slice(0, p.slug.length) ? p.slug : autoSlug }));
                }}
                placeholder="e.g. 10-Session Punchcard" />
              <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth select label="Plan Type" value={planForm.planType}
                onChange={e => setPlanForm(p => ({ ...p, planType: e.target.value }))}>
                {PLAN_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
              </TextField>
              <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth select label="Athlete Type" value={planForm.athleteType}
                onChange={e => setPlanForm(p => ({ ...p, athleteType: e.target.value }))}>
                {ATHLETE_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
              </TextField>
              {/* Per-plan waiver gate. When set, purchase of this plan opens
                  the waiver dialog if the buyer doesn't have a current
                  acceptance of the selected document. Leave blank for no gate. */}
              <TextField
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                fullWidth select label="Required Waiver"
                value={planForm.waiverDocId || ''}
                onChange={e => setPlanForm(p => ({ ...p, waiverDocId: e.target.value }))}
                helperText={
                  waiverDocs.length === 0
                    ? 'No eligible documents. Create an "accept" or "signature" document in Admin → Documents first.'
                    : 'Buyers must accept this document before purchase. Managed in Admin → Documents.'
                }
              >
                <MenuItem value="">— No waiver required —</MenuItem>
                {waiverDocs.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                    {d.expirationDays > 0 ? ` (${d.expirationDays}-day expiration)` : ' (never expires)'}
                  </MenuItem>
                ))}
              </TextField>
              <Box>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, ml: 0.5 }}>Level</Typography>
                <ToggleButtonGroup
                  value={planForm.level || []}
                  onChange={(_, newValue) => {
                    if (!newValue) { setPlanForm(p => ({ ...p, level: [] })); return; }
                    const wasAll = (planForm.level || []).includes('all');
                    const isAllNow = newValue.includes('all');
                    let next = newValue;
                    if (isAllNow && !wasAll) {
                      next = ['all'];
                    } else if (isAllNow && wasAll && newValue.length > 1) {
                      next = newValue.filter(v => v !== 'all');
                    }
                    setPlanForm(p => ({ ...p, level: next }));
                  }}
                  size="small"
                  sx={{
                    flexWrap: 'wrap', gap: 1, p: 1, backgroundColor: '#eeeeee', borderRadius: 2,
                    '& .MuiToggleButtonGroup-grouped': {
                      border: '1px solid #ccc',
                      borderRadius: '999px !important',
                      mx: 0,
                      px: 2.5,
                      py: 0.75,
                      textTransform: 'capitalize',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      backgroundColor: '#fff',
                      color: '#333',
                      '&:hover': {
                        backgroundColor: '#f0f6fc',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        border: '1px solid #1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        },
                      },
                    },
                  }}
                >
                  {LEVELS.map(l => (
                    <ToggleButton key={l} value={l}>{l}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="Price *" type="number" value={planForm.price}
                  onChange={e => setPlanForm(p => ({ ...p, price: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  inputProps={{ min: 0, step: 0.01 }} />
                <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="Sessions Included" type="number" value={planForm.sessionsIncluded}
                  onChange={e => setPlanForm(p => ({ ...p, sessionsIncluded: e.target.value }))}
                  inputProps={{ min: 1 }} />
                <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="Sort Order" type="number" value={planForm.sortOrder}
                  onChange={e => setPlanForm(p => ({ ...p, sortOrder: e.target.value }))}
                  helperText="Position within the plan group; lower numbers first" />
              </Box>
              {planForm.planType !== 'recurring' && planForm.athleteType === 'adult' && (
                <FormControlLabel
                  control={<Switch checked={planForm.allowMiniReg === true}
                    onChange={e => setPlanForm(p => ({ ...p, allowMiniReg: e.target.checked }))} />}
                  label={planForm.allowMiniReg
                    ? 'Quick Checkout enabled — visitors can buy without an account'
                    : 'Allow Quick Checkout (no account needed)'} />
              )}
              {planForm.planType === 'recurring' && (
                <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth select label="Unused Sessions" value={String(planForm.rollover)}
                  onChange={e => setPlanForm(p => ({ ...p, rollover: e.target.value === 'true' }))}>
                  <MenuItem value="true">Roll over (add on top each month)</MenuItem>
                  <MenuItem value="false">Reset (zero out and start fresh each month)</MenuItem>
                </TextField>
              )}
              {planForm.planType === 'recurring' && (
                <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="Stripe Price ID"
                  value={planForm.stripePriceId}
                  onChange={e => setPlanForm(p => ({ ...p, stripePriceId: e.target.value.trim() }))}
                  placeholder="price_1...."
                  helperText="Required for recurring plans. Copy from Stripe Dashboard > Products (Live mode)."
                />
              )}
              <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth select label="Plan Group" value={planForm.planGroup || 'Session Packs'}
                onChange={e => setPlanForm(p => ({ ...p, planGroup: e.target.value }))}>
                {planGroups.map(g => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
              </TextField>
              <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="Description (optional)" value={planForm.description}
                onChange={e => setPlanForm(p => ({ ...p, description: e.target.value }))}
                multiline rows={2} />
              <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} fullWidth label="URL Slug"
                value={planForm.slug}
                onChange={e => setPlanForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-') }))}
                helperText={`Share link: ${window?.location?.origin || 'https://app.torchvolleyball.com'}/p/${planForm.slug || 'your-slug'}`}
                placeholder="e.g. junior-10-pack" />
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, backgroundColor: '#f9f9f9' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!planForm.promptReserve}
                      onChange={e => setPlanForm(p => ({ ...p, promptReserve: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>After purchase, prompt clients to reserve a class</Typography>}
                />
                {planForm.promptReserve && (
                  <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1, mt: 1 }} fullWidth select
                    label="Class Type to Reserve"
                    value={planForm.reserveClassType}
                    onChange={e => setPlanForm(p => ({ ...p, reserveClassType: e.target.value }))}>
                    <MenuItem value="">— Any class —</MenuItem>
                    {CLASS_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={closePlanDialog}>Cancel</Button>
            <Button onClick={handleSavePlan} variant="contained" disabled={saving} sx={{ backgroundColor: '#1E3A5F' }}>
              {saving ? 'Saving...' : editingPlanId ? 'Save Changes' : 'Create Plan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Group Dialog */}
        <Dialog open={groupDialog} onClose={() => setGroupDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>
            {editingGroup ? 'Edit Group' : 'Create New Group'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1, mt: 1 }}
                       label="Group Name" fullWidth required
                       value={groupForm.name}
                       onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))}
                       helperText="Shown as the section header on the Plans and Training Plans pages." />
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                       label="Display Order" type="number" fullWidth
                       value={groupForm.displayOrder}
                       onChange={e => setGroupForm(p => ({ ...p, displayOrder: e.target.value }))}
                       helperText="Lower numbers display first. Use any positive integer (e.g. 1, 2, 3, 10, 20, 99)." />
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                       label="Description" fullWidth multiline minRows={2}
                       value={groupForm.description}
                       onChange={e => setGroupForm(p => ({ ...p, description: e.target.value }))}
                       helperText="Optional. Shown below the group header to explain what this group is for." />
            {editingGroup && (
              <Button color="error" startIcon={<DeleteIcon />}
                      onClick={() => { handleDeleteGroup(editingGroup); setGroupDialog(false); }}
                      sx={{ alignSelf: 'flex-start', mt: 1 }}>
                Delete Group
              </Button>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
            <Button onClick={() => setGroupDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveGroup} variant="contained" sx={{ backgroundColor: '#1E3A5F' }}>
              {editingGroup ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}

export default Payments;
